import { useState, useEffect, useCallback } from 'react';
import { login, register, signOut, updateProfile as updateUserProfile } from '../lib/auth';
import { getCurrentUser } from '../lib/localDatabase';
import { User } from '../types/user';
import { addAuditLog } from '../lib/auditLog';

interface LoginData {
  email: string;
  password: string;
  isMicrosoftAuth?: boolean;
  microsoftUser?: any;
}

interface RegisterData {
  email: string;
  password: string;
  name: string;
  company?: string;
  organizationName?: string;
  organizationPlan?: 'free' | 'starter' | 'professional' | 'enterprise';
  role?: 'admin' | 'technician';
  adminCode?: string;
}

interface ProfileUpdate {
  name?: string;
  company?: string;
}

// Local storage keys
const CURRENT_USER_KEY = 'installtrack_current_user';
const OFFLINE_USERS_KEY = 'installtrack_offline_users';

export const useLocalAuth = () => {
  const [user, setUser] = useState<User | null>(getCurrentUser());
  const [loading, setLoading] = useState(false);

  const handleLogin = useCallback(async (data: LoginData) => {
    setLoading(true);
    try {
      // Use the local login function which handles both online and offline modes
      const loggedInUser = await login(data);
      setUser(loggedInUser);
      return loggedInUser;
    } catch (error) {
      console.error('Error logging in user:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRegister = useCallback(async (data: RegisterData) => {
    setLoading(true);
    try {
      // Use the local register function which handles both online and offline modes
      const newUser = await register(data);
      setUser(newUser);
      
      // Log the registration
      addAuditLog('create', 'user', `New user registered: ${newUser.email}`, newUser.id);
      
      return newUser;
    } catch (error) {
      console.error('Error registering user:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSignOut = useCallback(async () => {
    setLoading(true);
    try {
      // Log the logout
      const currentUser = getCurrentUser();
      if (currentUser) {
        addAuditLog('logout', 'user', `User logged out: ${currentUser.email}`, currentUser.id);
      }
      
      await signOut();
      setUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const initializeAuth = useCallback(() => {
    try {
      const storedUser = getCurrentUser();
      setUser(storedUser);
    } catch (error) {
      console.error('Error initializing auth:', error);
    }
    return Promise.resolve();
  }, []);

  const updateProfile = useCallback(async (updates: ProfileUpdate) => {
    if (!user) return false;
    
    try {
      // Update user profile
      const success = await updateUserProfile(updates);
      
      if (success) {
        // Update local state with the new profile data
        setUser(prev => prev ? { ...prev, ...updates } : null);
      }
      
      return success;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }, [user]);

  return {
    user,
    loading,
    login: handleLogin,
    register: handleRegister,
    signOut: handleSignOut,
    initializeAuth,
    updateProfile
  };
};

// Helper functions
export const getCurrentUser = (): User | null => {
  try {
    const userStr = localStorage.getItem(CURRENT_USER_KEY);
    if (!userStr) return null;
    
    return JSON.parse(userStr, (key, value) => {
      if (key === 'lastLogin' && value) {
        return new Date(value);
      }
      return value;
    });
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

export const setCurrentUser = (user: User): void => {
  try {
    localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user));
  } catch (error) {
    console.error('Error setting current user:', error);
  }
};

export const clearCurrentUser = (): void => {
  try {
    localStorage.removeItem(CURRENT_USER_KEY);
  } catch (error) {
    console.error('Error clearing current user:', error);
  }
};